import { useQuery } from "@tanstack/react-query";
import { apiRequest, ApiError } from "@/lib/api-client";
import { brokerFixtures } from "@/mocks/fixtures/brokers";
import type { BrokerSummary } from "@/features/appointment/types";

/**
 * Returns the list of brokers (users) in the current tenant.
 *
 * TODO: replace with real GET /api/v1/users when that endpoint lands.
 * For now we attempt the call (so MSW handlers can answer in dev) and
 * fall back to a hardcoded list if the endpoint 404s — that way the
 * Select still renders something even in pure-backend mode.
 */
export const brokersKey = ["brokers"] as const;

const FALLBACK_BROKERS: readonly BrokerSummary[] = [
  { id: 1, fullName: "Tôi", role: "OWNER" },
];

export function useBrokers() {
  return useQuery({
    queryKey: brokersKey,
    queryFn: async () => {
      try {
        // /users/brokers is the slim shape — any authenticated user
        // can call it. The full /users endpoint is OWNER/MANAGER only
        // and isn't appropriate for AssignedBroker dropdowns.
        return await apiRequest<BrokerSummary[]>("/users/brokers");
      } catch (error) {
        // Backend hasn't shipped /users/brokers yet. Fall back to the
        // legacy /users alias for now, then to a hardcoded placeholder
        // if both 404.
        if (error instanceof ApiError && error.status === 404) {
          try {
            return await apiRequest<BrokerSummary[]>("/users");
          } catch (innerError) {
            if (innerError instanceof ApiError && innerError.status === 404) {
              return [...FALLBACK_BROKERS];
            }
            throw innerError;
          }
        }
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/** Exposed so MSW handlers + tests can reuse the canonical list. */
export const BROKER_FIXTURES = brokerFixtures;
